import { useEffect, useRef, useState } from "react";
import Footer from "@/components/Footer";
import { BrandMotionStyles } from "@/components/brand/IsoMarket";
import { Horizon, IsoLandscape, LandscapeStyles } from "@/components/brand/IsoLandscape";
import { PressButton, SignupBar, goToSignup } from "@/components/brand/Press";
import { Bento } from "./Bento";
import { Connectors } from "./Connectors";
import OctermLogoBlue from "@/octerm-full-blue.svg";
import { ChevronDown } from "lucide-react";
import "@/index.css";

// The revenue optimization landing page. One message: how each of your facilities is performing in
// its market, and what that means for revenue. The brand book's isometric market is the hero; each
// section's product panel is drawn in the app's own parts.

function Nav() {
  // Clear over the landscape, white once the page scrolls.
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Phones: the bar holds Sign in while the hero's signup button is in view. When that button has
  // passed under the bar, the bar's own button rises into Sign in's place, as if it had come up from
  // the hero; scrolling back swaps them again. The hero button itself just scrolls with the page.
  // Only transform and opacity change, so the swap runs smoothly however busy the page is.
  const headerRef = useRef<HTMLElement>(null);
  const [docked, setDocked] = useState(false);
  useEffect(() => {
    const hero = document.querySelector("#hero-cta button[type=submit]");
    const header = headerRef.current;
    if (!hero || !header) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry && setDocked(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { rootMargin: `-${header.offsetHeight}px 0px 0px 0px` },
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);
  return (
    <header ref={headerRef} className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${solid ? "border-slate-200 bg-white/95 backdrop-blur-sm" : "border-transparent bg-transparent"}`}>
      <style>{`
        @media (max-width: 767px) {
          .nav-swap > * { grid-area: 1 / 1; transition: transform 420ms cubic-bezier(0.2, 0.9, 0.2, 1), opacity 220ms ease-out; will-change: transform, opacity; }
          .nav-swap .nav-cta { opacity: 0; transform: translateY(32px) scale(0.92); pointer-events: none; }
          .nav-swap[data-docked="true"] .nav-cta { opacity: 1; transform: none; pointer-events: auto; }
          .nav-swap[data-docked="true"] .nav-signin { opacity: 0; transform: translateY(-16px) scale(0.94); pointer-events: none; }
        }
        @media (max-width: 767px) and (prefers-reduced-motion: reduce) { .nav-swap > * { transition: opacity 150ms ease-out; } }
      `}</style>
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <a href="/" className="flex items-center">
          <img src={OctermLogoBlue} alt="Octerm" className="h-6 w-auto" />
        </a>
        {/* One slot on phones (both buttons stacked in it, right-aligned), a row on wider screens. */}
        <div data-docked={docked} className="nav-swap grid justify-items-end md:flex md:items-center md:gap-3">
          <span className="nav-signin flex">
            <PressButton href="https://app.octerm.com/" tone="secondary">Sign in</PressButton>
          </span>
          <span className="nav-cta flex">
            <PressButton href="#demo" onClick={(e) => goToSignup(e, "demo", "demo-email")}>See my facilities</PressButton>
          </span>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative h-[100svh] min-h-[640px] overflow-hidden bg-white">
      <style>{`
        @keyframes hero-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .hero-rise { animation: hero-rise 800ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        @keyframes hero-nudge { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(5px); } }
        .hero-nudge { animation: hero-nudge 1.8s ease-in-out 3s infinite; }
        .hero-glass {
          background: rgba(255, 255, 255, 0.62);
          -webkit-backdrop-filter: blur(10px) saturate(1.1);
          backdrop-filter: blur(10px) saturate(1.1);
          --feather-x: linear-gradient(to right, transparent, #000 64px, #000 calc(100% - 64px), transparent);
          --feather-y: linear-gradient(to bottom, transparent, #000 56px, #000 calc(100% - 56px), transparent);
          -webkit-mask-image: var(--feather-x), var(--feather-y);
          -webkit-mask-composite: source-in;
          mask-image: var(--feather-x), var(--feather-y);
          mask-composite: intersect;
        }
        /* Only the upright layout needs it, where the copy sits over the market and trees. The wide
           layout keeps the scenery out from behind the copy already. */
        @media (min-aspect-ratio: 1/1) { .hero-glass { display: none; } }
      `}</style>
      <LandscapeStyles />
      <IsoLandscape className="absolute inset-0 h-full w-full" />
      {/* The country fades into the page below, with a cue to keep going. */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-b from-white/0 via-white/70 to-white" />
      <a href="#features" className="hero-rise group absolute inset-x-0 bottom-6 z-10 mx-auto flex w-max flex-col items-center gap-1 text-sm font-medium text-slate-600 hover:text-logo" style={{ animationDelay: "2600ms" }}>
        See how it works
        <ChevronDown className="hero-nudge h-5 w-5 text-logo" />
      </a>
      <div className="relative z-10 mx-auto max-w-6xl px-5 pt-[15svh] md:pt-[24svh]">
        <div className="relative w-fit max-w-full">
        {/* Frosted glass behind the copy on upright screens: the landscape blurred and washed light,
            feathered out on every side so it has no edge. */}
        <div aria-hidden className="hero-glass hero-rise pointer-events-none absolute -inset-x-16 -inset-y-14" style={{ animationDelay: "150ms" }} />
        <h1 className="relative hero-rise text-[clamp(40px,13.5vw,56px)] font-semibold leading-none tracking-[-0.035em] text-logo md:text-[80px]" style={{ animationDelay: "250ms" }}>
          Stop guessing.
        </h1>
        <p className="relative hero-rise mt-6 max-w-md text-pretty text-lg leading-relaxed text-slate-700" style={{ animationDelay: "400ms" }}>
          Octerm takes the guesswork out of self-storage pricing. See the past, present, and future.
        </p>
        <div id="hero-cta" className="relative hero-rise mt-8 max-w-[30rem]" style={{ animationDelay: "550ms" }}>
          <SignupBar />
        </div>
        </div>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section id="demo" className="relative flex min-h-[100svh] flex-col overflow-hidden bg-gradient-to-b from-white via-[#f6f9fa] to-[#eaf1f4]">
      {/* The page closes as it opened: the same sky, with the mountains on the horizon. */}
      <Horizon fade="#f9fafb" className="pointer-events-none absolute inset-x-0 bottom-0 h-[20%] w-full sm:h-[34%]" />
      <div className="relative mx-auto flex w-full max-w-3xl flex-1 flex-col px-5 pb-[18svh] pt-20 text-center md:justify-center md:pt-28">
        <h2 className="text-balance text-[clamp(40px,8vw,72px)] font-semibold leading-[1.02] tracking-[-0.035em] text-logo">See how your facilities stack up.</h2>
        <p className="mx-auto mt-4 max-w-xl text-pretty text-lg leading-relaxed text-slate-600 md:mt-6 md:text-xl">
          Confirm your work email and see one of your facilities against its market.
        </p>
        <div className="mx-auto mt-8 w-full max-w-2xl text-left md:mt-12">
          <SignupBar id="demo-email" size="xl" />
        </div>
      </div>
    </section>
  );
}

export function RevenueLandingPage() {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <BrandMotionStyles />
      <Nav />
      <Hero />
      <Bento />
      <Connectors />
      <Closing />
      <Footer variant="dark" />
    </div>
  );
}
